/**
 * HsForm
 * HubSpot埋め込みフォーム（.js-hsform）の読み込み中スピナー表示＋完了後フェードイン。
 *
 * フォーム本体はクロスオリジンiframeのため中身のイベントは取れない。
 * iframe要素の高さ（HubSpotが描画／reCAPTCHA後追い／バリデーション等で段階的に変化）を
 * ResizeObserverで監視し、コンテナ高さを常時追従させる（CSSの height transition で全リサイズを滑らかに）。
 * 一度だけ測って合わせると、中途半端な高さ→後追いで跳ねる（ガクッ）ため、追従方式にしている。
 * min-height（CSS・スピナー用）を床として残すので、低い中途高さでも縮小バウンドしない。
 * JS無効時はCSS既定でフォームが表示される（is-loading を付けるのはここだけ）。
 *
 * @example
 * <div class="p-hsform js-hsform">
 *   <div class="p-hsform-loading"><span class="p-hsform-spinner"></span></div>
 *   <div class="hs-form-frame" data-form-id="…"></div>
 * </div>
 */
export default class HsForm {
  constructor() {
    document.querySelectorAll('.js-hsform').forEach((root) => this.setup(root));
  }

  /**
   * @param {HTMLElement} root .js-hsform
   */
  setup(root) {
    const frame = root.querySelector('.hs-form-frame');
    if (!frame) {
      return;
    }

    root.classList.add('is-loading');

    let revealed = false;

    /**
     * iframe高さにコンテナを追従。初回はスピナー（is-loading）を解除してフェードイン開始。
     * @param {number} height iframeの現在高さ(px)
     */
    const apply = (height) => {
      if (height <= 40) {
        return;
      }
      if (!revealed) {
        revealed = true;
        root.style.height = `${root.offsetHeight}px`; // 現在(スピナー用min-height)を起点にtransitionさせる
        root.classList.remove('is-loading');
        void root.offsetHeight; // reflowで開始値を確定
      }
      // min-height(CSS)が床なので、これより低くしても縮まない＝中途高さでのバウンド無し
      root.style.height = `${height}px`;
    };

    const attach = (iframe) => {
      const read = () => apply(Math.round(iframe.getBoundingClientRect().height));
      if ('ResizeObserver' in window) {
        new ResizeObserver(read).observe(iframe);
      }
      iframe.addEventListener('load', () => window.setTimeout(read, 100));
      // ResizeObserver非対応／取りこぼし用のポーリング（表示開始までの保険）
      let count = 0;
      const timer = window.setInterval(() => {
        read();
        if (revealed || (count += 1) > 50) {
          window.clearInterval(timer);
        }
      }, 200);
    };

    const existing = frame.querySelector('iframe');
    if (existing) {
      attach(existing);
    } else {
      // iframe はスクリプト読み込み後に挿入されるため出現を監視
      const observer = new MutationObserver(() => {
        const iframe = frame.querySelector('iframe');
        if (iframe) {
          observer.disconnect();
          attach(iframe);
        }
      });
      observer.observe(frame, { childList: true, subtree: true });
    }

    // 最終フォールバック：高さを検出できなくても必ず表示（auto＝min-height床のまま）
    window.setTimeout(() => {
      if (revealed) {
        return;
      }
      revealed = true;
      root.classList.remove('is-loading');
      root.style.height = '';
    }, 8000);
  }
}
